import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
    TbFileTypePdf, TbX, TbMaximize, TbRefresh,
    TbLayoutAlignLeft, TbLayoutAlignCenter, TbLayoutAlignRight,
    TbLayoutAlignTop, TbLayoutAlignMiddle, TbLayoutAlignBottom,
} from "react-icons/tb";
import { GrClearOption } from "react-icons/gr";
import { SettingRow, TextArea, Toggle } from "./SettingsControls";
import Popup from "src/components/Popup/Popup";
import type { PdfSettings } from "./useSettings";

const TEMPLATE_VARS = [
    "{praxis.name}", "{praxis.address}", "{praxis.plz}", "{praxis.city}",
    "{praxis.specialization}", "{praxis.phone}", "{praxis.email}",
    "{doctor.salutation}", "{doctor.title}", "{doctor.name}",
    "{invoice.id}", "{invoice.date}", "{invoice.dueDate}",
];

const FOOTER_VARS = [...TEMPLATE_VARS, "{praxis.accountHolder}", "{praxis.iban}", "{praxis.bic}"];
// Texts printed per invoice — the recipient is known there (pageLayout.ts resolveVars).
const RECIPIENT_TEXT_VARS = [...TEMPLATE_VARS, "{recipient.name}", "{recipient.firstName}", "{recipient.lastName}"];


const FONT_SIZES = ["6", "7", "7.5", "8", "9", "10", "11", "12"];

const COLORS = [
    "#000000", "#222222", "#444444", "#666666", "#888888", "#aaaaaa",
    "#1e3a5f", "#1d4ed8", "#0891b2", "#059669", "#65a30d",
    "#d97706", "#dc2626", "#9333ea", "#be185d",
];

function NullableColorSwatches({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    const isCustom = value !== "" && !COLORS.includes(value);
    return (
        <div className="pdf-color-swatches">
            <button
                type="button"
                className={`pdf-color-swatch pdf-color-swatch--none${value === "" ? " pdf-color-swatch--active" : ""}`}
                onClick={() => onChange("")}
                title="None"
            />
            {COLORS.map((c) => (
                <button
                    key={c}
                    type="button"
                    className={`pdf-color-swatch${value === c ? " pdf-color-swatch--active" : ""}`}
                    style={{ "--swatch": c } as React.CSSProperties}
                    onClick={() => onChange(c)}
                    title={c}
                />
            ))}
            <label className={`pdf-color-swatch pdf-color-swatch--custom${isCustom ? " pdf-color-swatch--active" : ""}`} title="Custom color">
                <input type="color" value={isCustom ? value : "#000000"} onChange={(e) => onChange(e.target.value)} />
            </label>
        </div>
    );
}

function PxInput({ value, onChange, min = 0 }: { value: number; onChange: (v: number) => void; min?: number }) {
    return (
        <span className="pdf-px-input">
            <input
                type="number"
                min={min}
                className="settings-input settings-input--small"
                value={value}
                onChange={(e) => onChange(Math.max(min, parseInt(e.target.value) || 0))}
            />
            <span className="pdf-px-label">px</span>
        </span>
    );
}

function BoxStyleRow({
    borderColor, borderWidth, paddingH, paddingV, marginValue, marginLabel,
    labels,
    onBorderColor, onBorderWidth, onPaddingH, onPaddingV, onMargin,
}: {
    borderColor: string; borderWidth: number;
    paddingH: number; paddingV: number; marginValue: number; marginLabel: string;
    labels: { border: string; width: string; insetH: string; insetV: string };
    onBorderColor: (v: string) => void; onBorderWidth: (v: number) => void;
    onPaddingH: (v: number) => void; onPaddingV: (v: number) => void; onMargin: (v: number) => void;
}) {
    return (
        <div className="pdf-box-style">
            <div className="pdf-box-style__row">
                <span className="pdf-box-style__label">{labels.border}</span>
                <NullableColorSwatches value={borderColor} onChange={onBorderColor} />
                <label className="pdf-box-style__field">{labels.width} <PxInput value={borderWidth} onChange={onBorderWidth} /></label>
            </div>
            <div className="pdf-box-style__row">
                <label className="pdf-box-style__field">{labels.insetH} <PxInput value={paddingH} onChange={onPaddingH} /></label>
                <label className="pdf-box-style__field">{labels.insetV} <PxInput value={paddingV} onChange={onPaddingV} /></label>
                <label className="pdf-box-style__field">{marginLabel} <PxInput value={marginValue} onChange={onMargin} /></label>
            </div>
        </div>
    );
}

function VarChips({ onInsert, vars = TEMPLATE_VARS }: { onInsert: (v: string) => void; vars?: string[] }) {
    return (
        <div className="pdf-var-chips">
            {vars.map((v) => (
                <button key={v} type="button" className="pdf-var-chip" onClick={() => onInsert(v)}>
                    {v}
                </button>
            ))}
        </div>
    );
}

function FontStyleRow({
    color, size, weight, labels,
    onColor, onSize, onWeight,
}: {
    color: string; size: number; weight: "normal" | "bold";
    labels: { size: string; bold: string };
    onColor: (v: string) => void; onSize: (v: number) => void; onWeight: (v: "normal" | "bold") => void;
}) {
    const active = color || "#555555";
    const isCustom = !!color && !COLORS.includes(color);
    return (
        <div className="pdf-font-style">
            <div className="pdf-color-swatches">
                {COLORS.map((c) => (
                    <button
                        key={c}
                        type="button"
                        className={`pdf-color-swatch${active === c ? " pdf-color-swatch--active" : ""}`}
                        style={{ "--swatch": c } as React.CSSProperties}
                        onClick={() => onColor(c)}
                        title={c}
                    />
                ))}
                <label className={`pdf-color-swatch pdf-color-swatch--custom${isCustom ? " pdf-color-swatch--active" : ""}`} title="Custom color">
                    <input type="color" value={isCustom ? color : "#555555"} onChange={(e) => onColor(e.target.value)} />
                </label>
            </div>
            <label>
                {labels.size}
                <select className="settings-select settings-select--small" value={String(size)} onChange={(e) => onSize(parseFloat(e.target.value))}>
                    {FONT_SIZES.map((s) => <option key={s} value={s}>{s}px</option>)}
                </select>
            </label>
            <label className="pdf-font-bold-label">
                {labels.bold}
                <Toggle value={weight === "bold"} onChange={(v) => onWeight(v ? "bold" : "normal")} />
            </label>
        </div>
    );
}

type HAlign = "left" | "center" | "right";
type VAlign = "top" | "middle" | "bottom";

function AlignRow({
    hAlign, vAlign, labels, onHAlign, onVAlign,
}: {
    hAlign: HAlign; vAlign: VAlign;
    labels: { horizontal: string; vertical: string; left: string; center: string; right: string; top: string; middle: string; bottom: string };
    onHAlign: (v: HAlign) => void; onVAlign: (v: VAlign) => void;
}) {
    return (
        <div className="pdf-align-row">
            <div className="pdf-align-group">
                <span className="pdf-align-group__label">{labels.horizontal}</span>
                <button type="button" className={`pdf-align-group__btn${hAlign === "left" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onHAlign("left")} title={labels.left}><TbLayoutAlignLeft /></button>
                <button type="button" className={`pdf-align-group__btn${hAlign === "center" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onHAlign("center")} title={labels.center}><TbLayoutAlignCenter /></button>
                <button type="button" className={`pdf-align-group__btn${hAlign === "right" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onHAlign("right")} title={labels.right}><TbLayoutAlignRight /></button>
            </div>
            <div className="pdf-align-group">
                <span className="pdf-align-group__label">{labels.vertical}</span>
                <button type="button" className={`pdf-align-group__btn${vAlign === "top" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onVAlign("top")} title={labels.top}><TbLayoutAlignTop /></button>
                <button type="button" className={`pdf-align-group__btn${vAlign === "middle" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onVAlign("middle")} title={labels.middle}><TbLayoutAlignMiddle /></button>
                <button type="button" className={`pdf-align-group__btn${vAlign === "bottom" ? " pdf-align-group__btn--active" : ""}`} onClick={() => onVAlign("bottom")} title={labels.bottom}><TbLayoutAlignBottom /></button>
            </div>
        </div>
    );
}

interface Props {
    pdfSettings: PdfSettings;
    /** False when `pdfSettings` is just the inherited praxis-wide default, not something
     *  this doctor/admin has saved themselves yet (see settingsController.getSettings). */
    pdfSettingsIsOwn?: boolean;
    hideCategoryLabels?: boolean;
    saved: boolean;
    dirty?: boolean;
    onChange: (v: PdfSettings) => void;
    onSave: () => void;
    onClear: () => void;
}

type PdfTab = "generic" | "header" | "footer" | "body";

export function PdfSettings({ pdfSettings: s, pdfSettingsIsOwn, hideCategoryLabels, saved, dirty, onChange, onSave, onClear }: Props) {
    const { t } = useTranslation();
    const [previewUrl, setPreviewUrl]       = useState<string | null>(null);
    const [previewing, setPreviewing]       = useState(false);
    const [modalOpen, setModalOpen]         = useState(false);
    const [previewOpen, setPreviewOpen]     = useState(false);
    const [confirmClear, setConfirmClear]   = useState(false);
    const [activeTab, setActiveTab]         = useState<PdfTab>("generic");
    const [dragPos, setDragPos]             = useState<{ x: number; y: number } | null>(null);
    const logoInputRef  = useRef<HTMLInputElement>(null);
    const previewRef    = useRef<HTMLDivElement>(null);
    const dragOffset    = useRef<{ x: number; y: number } | null>(null);
    const latestSettings = useRef(s);

    const handleDragStart = (e: React.MouseEvent) => {
        if (!previewRef.current) return;
        const rect = previewRef.current.getBoundingClientRect();
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

        const onMove = (e: MouseEvent) => {
            if (!dragOffset.current) return;
            setDragPos({
                x: Math.max(0, Math.min(window.innerWidth  - 380, e.clientX - dragOffset.current.x)),
                y: Math.max(0, Math.min(window.innerHeight -  60, e.clientY - dragOffset.current.y)),
            });
        };
        const onUp = () => {
            dragOffset.current = null;
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup",   onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup",   onUp);
    };

    // Reset position when the floating window is closed
    useEffect(() => { if (!previewOpen) setDragPos(null); }, [previewOpen]);

    const boxLabels = { border: t("pdfSettings.border"), width: t("pdfSettings.borderWidth"), insetH: t("pdfSettings.insetH"), insetV: t("pdfSettings.insetV") };
    const fontLabels = { size: t("pdfSettings.fontSize"), bold: t("pdfSettings.bold") };
    const alignLabels = {
        horizontal: t("pdfSettings.alignHorizontal"), vertical: t("pdfSettings.alignVertical"),
        left: t("pdfSettings.alignLeft"), center: t("pdfSettings.alignCenter"), right: t("pdfSettings.alignRight"),
        top: t("pdfSettings.alignTop"), middle: t("pdfSettings.alignMiddle"), bottom: t("pdfSettings.alignBottom"),
    };

    const [logoDims, setLogoDims] = useState<{ w: number; h: number } | null>(null);

    // Read natural dims from existing logo on mount / when logo changes
    const updateLogoDims = (src: string) => {
        if (!src) { setLogoDims(null); return; }
        const img = new Image();
        img.onload = () => setLogoDims({ w: img.naturalWidth, h: img.naturalHeight });
        img.src = src;
    };

    // Keep dims in sync when logo prop changes
    if (s.headerLogo && !logoDims) updateLogoDims(s.headerLogo);
    if (!s.headerLogo && logoDims) setLogoDims(null);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d")?.drawImage(img, 0, 0);
            setLogoDims({ w: img.naturalWidth, h: img.naturalHeight });
            onChange({
                ...s,
                headerLogo: canvas.toDataURL("image/png"),
                headerLogoSlot: s.headerLogoSlot === "none" ? "left" : s.headerLogoSlot,
            });
            URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
    };

    const set = <K extends keyof PdfSettings>(key: K, value: PdfSettings[K]) =>
        onChange({ ...s, [key]: value });

    const insertInto = (field: keyof PdfSettings) => (v: string) =>
        set(field, (s[field] as string) + v);

    const generatePreview = useCallback(async (settings: PdfSettings) => {
        setPreviewing(true);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/settings/pdf-preview`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ pdfSettings: settings, hideCategoryLabels }),
            });
            if (!res.ok) throw new Error("Preview failed");
            const blob = await res.blob();
            setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
        } catch {
            /* silently ignore */
        } finally {
            setPreviewing(false);
        }
    }, [hideCategoryLabels]);

    // Keep ref in sync so the debounce closure always uses the latest settings
    useEffect(() => { latestSettings.current = s; }, [s]);

    // Debounced live preview — triggers 1.2s after last settings change
    useEffect(() => {
        setPreviewing(true);
        const timer = setTimeout(() => generatePreview(latestSettings.current), 1200);
        return () => clearTimeout(timer);
    }, [s, generatePreview]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {modalOpen && previewUrl && createPortal(
                <div className="pdf-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pdf-modal__header">
                            <span>{t("pdfSettings.previewTitle")}</span>
                            <button className="pdf-modal__close" onClick={() => setModalOpen(false)}><TbX /></button>
                        </div>
                        <iframe className="pdf-modal__frame" src={previewUrl} title={t("pdfSettings.previewTitle")} />
                    </div>
                </div>,
                document.body,
            )}

            {confirmClear && (
                <Popup
                    title={t("pdfSettings.clearTitle")}
                    message={t("pdfSettings.clearMessage")}
                    onClose={() => setConfirmClear(false)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setConfirmClear(false) },
                        { label: t("pdfSettings.clearAll"),            variant: "danger",  onClick: () => { onClear(); setConfirmClear(false); } },
                    ]}
                />
            )}

            <div className="settings-section">
                <h2 className="settings-section__title">
                    <div className="settings-section__title-text">
                        <span>{t("pdfSettings.content")}</span>
                    </div>
                    <div className="settings-section__actions">
                        <button className="btn-outline btn-outline--secondary" onClick={() => setConfirmClear(true)}>
                            <GrClearOption className="btn-icon-left" />
                            {t("pdfSettings.clearAll")}
                        </button>
                        <button
                            className="btn-outline btn-outline--secondary pdf-preview-toggle-btn"
                            onClick={() => setPreviewOpen(true)}
                        >
                            <TbFileTypePdf className="btn-icon-left" />
                            {t("pdfSettings.previewPdf")}
                        </button>
                    </div>
                </h2>

                <div className="pdf-settings-layout">
                    <div className="pdf-settings-form">
                    <div className="settings-section__body">

                {!pdfSettingsIsOwn && (
                    <div className="settings-info-banner settings-info-banner--neutral">
                        <TbFileTypePdf />
                        <span>{t("pdfSettings.praxisDefaultNotice")}</span>
                    </div>
                )}

                {/* ── Tab bar ── */}
                <div className="settings-tabs">
                    {(["generic", "header", "footer", "body"] as PdfTab[]).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className={`settings-tab${activeTab === tab ? " settings-tab--active" : ""}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {t(`pdfSettings.tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
                        </button>
                    ))}
                </div>

                {/* ── Generic tab ── */}
                {activeTab === "generic" && (
                    <>
                        <SettingRow title={t("pdfSettings.showQrCode")} description={t("pdfSettings.showQrCodeDesc")}>
                            <Toggle value={s.showQrCode} onChange={(v) => set("showQrCode", v)} />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.showUstgNote")} description={t("pdfSettings.showUstgNoteDesc")}>
                            <Toggle value={s.showUstgNote} onChange={(v) => set("showUstgNote", v)} />
                        </SettingRow>
                    </>
                )}

                {/* ── Page Header tab ── */}
                {activeTab === "header" && (
                    <>
                        <p className="pdf-subsection-hint pdf-subsection-hint--tab">
                            {t("pdfSettings.headerHint")}
                        </p>
                        <SettingRow title={t("pdfSettings.headerRepeatOnEveryPage")} description={t("pdfSettings.headerRepeatOnEveryPageDesc")}>
                            <Toggle value={s.headerRepeatOnEveryPage} onChange={(v) => set("headerRepeatOnEveryPage", v)} />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.boxStyle")} column>
                            <BoxStyleRow
                                borderColor={s.headerBorderColor} borderWidth={s.headerBorderWidth}
                                paddingH={s.headerPaddingH} paddingV={s.headerPaddingV}
                                marginValue={s.headerMarginBottom} marginLabel={t("pdfSettings.marginBottom")}
                                labels={boxLabels}
                                onBorderColor={(v) => set("headerBorderColor", v)}
                                onBorderWidth={(v) => set("headerBorderWidth", v)}
                                onPaddingH={(v) => set("headerPaddingH", v)}
                                onPaddingV={(v) => set("headerPaddingV", v)}
                                onMargin={(v) => set("headerMarginBottom", v)}
                            />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.logo")} description={t("pdfSettings.logoDesc")} column>
                            <div className="pdf-logo-upload-row">
                                <div className="avatar-upload">
                                    <div className="avatar-upload__preview" onClick={() => logoInputRef.current?.click()}>
                                        {s.headerLogo
                                            ? <img src={s.headerLogo} alt="logo" />
                                            : <span>+</span>
                                        }
                                        <div className="avatar-upload__overlay">{t("form.change")}</div>
                                    </div>
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="sr-only"
                                        onChange={handleLogoChange}
                                    />
                                    {s.headerLogo && (
                                        <button className="avatar-upload__remove" onClick={() => { set("headerLogo", ""); setLogoDims(null); }}>
                                            {t("form.remove")}
                                        </button>
                                    )}
                                </div>
                                {s.headerLogo && (
                                    <div className="pdf-logo-size">
                                        {logoDims && (
                                            <span className="pdf-logo-size__dims">
                                                {logoDims.w} × {logoDims.h} px
                                            </span>
                                        )}
                                        <label className="pdf-logo-size__label">{t("pdfSettings.logoSize")}</label>
                                        <div className="pdf-logo-size__slider-row">
                                            <input
                                                type="range"
                                                min={16}
                                                max={80}
                                                step={1}
                                                value={s.headerLogoSize ?? 34}
                                                onChange={(e) => set("headerLogoSize", Number(e.target.value))}
                                                className="pdf-logo-size__slider"
                                            />
                                            <span className="pdf-logo-size__value">{s.headerLogoSize ?? 34} px</span>
                                        </div>
                                        <div className="pdf-logo-size__minmax">
                                            <span>16 px</span><span>80 px</span>
                                        </div>
                                        <label className="pdf-logo-size__label" title={t("pdfSettings.logoOffsetDesc")}>
                                            {t("pdfSettings.logoOffset")}
                                        </label>
                                        <div className="pdf-box-style__row">
                                            <label className="pdf-box-style__field">
                                                {t("pdfSettings.logoOffsetX")}
                                                <PxInput value={s.headerLogoOffsetX ?? 0} onChange={(v) => set("headerLogoOffsetX", v)} min={-999} />
                                            </label>
                                            <label className="pdf-box-style__field">
                                                {t("pdfSettings.logoOffsetY")}
                                                <PxInput value={s.headerLogoOffsetY ?? 0} onChange={(v) => set("headerLogoOffsetY", v)} min={-999} />
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.logoPosition")} description={t("pdfSettings.logoPositionDesc")}>
                            <select
                                className="settings-select"
                                value={s.headerLogoSlot}
                                onChange={(e) => set("headerLogoSlot", e.target.value as PdfSettings["headerLogoSlot"])}
                            >
                                <option value="none">{t("pdfSettings.logoSlotNone")}</option>
                                <option value="left">{t("pdfSettings.logoSlotLeft")}</option>
                                <option value="center">{t("pdfSettings.logoSlotCenter")}</option>
                                <option value="right">{t("pdfSettings.logoSlotRight")}</option>
                            </select>
                        </SettingRow>
                        {(["left", "center", "right"] as const).map((slot) => {
                            const isLogoCol = s.headerLogo && s.headerLogoSlot === slot;
                            const textKey   = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}` as "headerLeft" | "headerCenter" | "headerRight";
                            const colorKey  = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}FontColor`  as keyof PdfSettings;
                            const sizeKey   = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}FontSize`   as keyof PdfSettings;
                            const weightKey = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}FontWeight` as keyof PdfSettings;
                            const alignKey  = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}Align`      as keyof PdfSettings;
                            const vAlignKey = `header${slot.charAt(0).toUpperCase() + slot.slice(1)}VAlign`     as keyof PdfSettings;
                            const colTitleKey = `col${slot.charAt(0).toUpperCase() + slot.slice(1)}` as "colLeft" | "colCenter" | "colRight";
                            return (
                                <SettingRow
                                    key={slot}
                                    title={t(`pdfSettings.${colTitleKey}`)}
                                    description={slot === "left" ? t("pdfSettings.colLeftDesc") : undefined}
                                    column
                                >
                                    {isLogoCol
                                        ? <p className="pdf-logo-col-notice">{t("pdfSettings.logoColNotice")}</p>
                                        : <>
                                            <TextArea value={s[textKey] as string} onChange={(v) => set(textKey, v)} rows={2} placeholder={slot === "left" ? t("pdfSettings.colPlaceholder") : undefined} />
                                            <VarChips onInsert={insertInto(textKey)} />
                                            <FontStyleRow
                                                color={s[colorKey] as string} size={s[sizeKey] as number} weight={s[weightKey] as "normal" | "bold"}
                                                labels={fontLabels}
                                                onColor={(v) => set(colorKey, v)} onSize={(v) => set(sizeKey, v)} onWeight={(v) => set(weightKey, v)}
                                            />
                                            <AlignRow
                                                hAlign={s[alignKey] as HAlign} vAlign={s[vAlignKey] as VAlign}
                                                labels={alignLabels}
                                                onHAlign={(v) => set(alignKey, v)} onVAlign={(v) => set(vAlignKey, v)}
                                            />
                                        </>
                                    }
                                </SettingRow>
                            );
                        })}
                    </>
                )}

                {/* ── Body text tab ── */}
                {activeTab === "body" && (
                    <>
                        <p className="pdf-subsection-hint pdf-subsection-hint--tab">
                            {t("pdfSettings.bodyHint")}
                        </p>
                        <SettingRow title={t("pdfSettings.bodyHeading")} description={t("pdfSettings.bodyHeadingDesc")} column>
                            <TextArea
                                value={s.bodyHeading}
                                onChange={(v) => set("bodyHeading", v)}
                                rows={2}
                                placeholder={t("pdfSettings.bodyHeadingPlaceholder")}
                            />
                            <div className="pdf-heading-format">
                                <button
                                    type="button"
                                    className={`pdf-heading-format__btn${s.bodyHeadingBold ? " pdf-heading-format__btn--active" : ""}`}
                                    onClick={() => set("bodyHeadingBold", !s.bodyHeadingBold)}
                                    title={t("pdfSettings.bold")}
                                ><b>B</b></button>
                                <button
                                    type="button"
                                    className={`pdf-heading-format__btn${s.bodyHeadingItalic ? " pdf-heading-format__btn--active" : ""}`}
                                    onClick={() => set("bodyHeadingItalic", !s.bodyHeadingItalic)}
                                    title={t("pdfSettings.italic")}
                                ><i>I</i></button>
                                <button
                                    type="button"
                                    className={`pdf-heading-format__btn${s.bodyHeadingUnderline ? " pdf-heading-format__btn--active" : ""}`}
                                    onClick={() => set("bodyHeadingUnderline", !s.bodyHeadingUnderline)}
                                    title={t("pdfSettings.underline")}
                                ><u>U</u></button>
                            </div>
                            <VarChips onInsert={insertInto("bodyHeading")} />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.bodyChargeLine")} description={t("pdfSettings.bodyChargeLineDesc")} column>
                            <TextArea
                                value={s.bodyChargeLine}
                                onChange={(v) => set("bodyChargeLine", v)}
                                rows={2}
                                placeholder={t("pdfSettings.bodyChargeLinePlaceholder")}
                            />
                            <VarChips onInsert={insertInto("bodyChargeLine")} vars={RECIPIENT_TEXT_VARS} />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.bodyPaymentTerms")} description={t("pdfSettings.bodyPaymentTermsDesc")} column>
                            <TextArea
                                value={s.bodyPaymentTerms}
                                onChange={(v) => set("bodyPaymentTerms", v)}
                                rows={3}
                                placeholder={t("pdfSettings.bodyPaymentTermsPlaceholder")}
                            />
                            <VarChips onInsert={insertInto("bodyPaymentTerms")} vars={RECIPIENT_TEXT_VARS} />
                        </SettingRow>
                    </>
                )}

                {/* ── Page Footer tab ── */}
                {activeTab === "footer" && (
                    <>
                        <p className="pdf-subsection-hint pdf-subsection-hint--tab">
                            {t("pdfSettings.footerHint")}
                        </p>
                        <SettingRow title={t("pdfSettings.boxStyle")} column>
                            <BoxStyleRow
                                borderColor={s.footerBorderColor} borderWidth={s.footerBorderWidth}
                                paddingH={s.footerPaddingH} paddingV={s.footerPaddingV}
                                marginValue={s.footerMarginTop} marginLabel={t("pdfSettings.marginTop")}
                                labels={boxLabels}
                                onBorderColor={(v) => set("footerBorderColor", v)}
                                onBorderWidth={(v) => set("footerBorderWidth", v)}
                                onPaddingH={(v) => set("footerPaddingH", v)}
                                onPaddingV={(v) => set("footerPaddingV", v)}
                                onMargin={(v) => set("footerMarginTop", v)}
                            />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.showBankDetails")} description={t("pdfSettings.showBankDetailsDesc")}>
                            <Toggle value={s.footerShowBank} onChange={(v) => set("footerShowBank", v)} />
                        </SettingRow>
                        <SettingRow title={t("pdfSettings.showAddress")} description={t("pdfSettings.showAddressDesc")}>
                            <Toggle value={s.footerShowAddress} onChange={(v) => set("footerShowAddress", v)} />
                        </SettingRow>
                        {(() => {
                            const autoMode = s.footerShowBank || s.footerShowAddress;
                            return (
                                <div className={autoMode ? "pdf-section--disabled" : ""}>
                                    <SettingRow title={t("pdfSettings.colLeft")} column>
                                        <TextArea value={s.footerLeft} onChange={(v) => set("footerLeft", v)} rows={2} disabled={autoMode} />
                                        <VarChips onInsert={insertInto("footerLeft")} vars={FOOTER_VARS} />
                                        <FontStyleRow
                                            color={s.footerLeftFontColor} size={s.footerLeftFontSize} weight={s.footerLeftFontWeight}
                                            labels={fontLabels}
                                            onColor={(v) => set("footerLeftFontColor", v)} onSize={(v) => set("footerLeftFontSize", v)} onWeight={(v) => set("footerLeftFontWeight", v)}
                                        />
                                        <AlignRow
                                            hAlign={s.footerLeftAlign} vAlign={s.footerLeftVAlign}
                                            labels={alignLabels}
                                            onHAlign={(v) => set("footerLeftAlign", v)} onVAlign={(v) => set("footerLeftVAlign", v)}
                                        />
                                    </SettingRow>
                                    <SettingRow title={t("pdfSettings.colCenter")} column>
                                        <TextArea value={s.footerCenter} onChange={(v) => set("footerCenter", v)} rows={2} disabled={autoMode} />
                                        <VarChips onInsert={insertInto("footerCenter")} vars={FOOTER_VARS} />
                                        <FontStyleRow
                                            color={s.footerCenterFontColor} size={s.footerCenterFontSize} weight={s.footerCenterFontWeight}
                                            labels={fontLabels}
                                            onColor={(v) => set("footerCenterFontColor", v)} onSize={(v) => set("footerCenterFontSize", v)} onWeight={(v) => set("footerCenterFontWeight", v)}
                                        />
                                        <AlignRow
                                            hAlign={s.footerCenterAlign} vAlign={s.footerCenterVAlign}
                                            labels={alignLabels}
                                            onHAlign={(v) => set("footerCenterAlign", v)} onVAlign={(v) => set("footerCenterVAlign", v)}
                                        />
                                    </SettingRow>
                                    <SettingRow title={t("pdfSettings.colRight")} column>
                                        <TextArea value={s.footerRight} onChange={(v) => set("footerRight", v)} rows={2} disabled={autoMode} />
                                        <VarChips onInsert={insertInto("footerRight")} vars={FOOTER_VARS} />
                                        <FontStyleRow
                                            color={s.footerRightFontColor} size={s.footerRightFontSize} weight={s.footerRightFontWeight}
                                            labels={fontLabels}
                                            onColor={(v) => set("footerRightFontColor", v)} onSize={(v) => set("footerRightFontSize", v)} onWeight={(v) => set("footerRightFontWeight", v)}
                                        />
                                        <AlignRow
                                            hAlign={s.footerRightAlign} vAlign={s.footerRightVAlign}
                                            labels={alignLabels}
                                            onHAlign={(v) => set("footerRightAlign", v)} onVAlign={(v) => set("footerRightVAlign", v)}
                                        />
                                    </SettingRow>
                                </div>
                            );
                        })()}
                    </>
                )}

                    </div>{/* closes settings-section__body (tabs + active panel) */}
                    </div>{/* end .pdf-settings-form */}

                    {/* ── Live preview panel ── */}
                    <div
                        ref={previewRef}
                        className={`pdf-live-preview${previewOpen ? " pdf-live-preview--open" : ""}`}
                        style={dragPos ? { top: dragPos.y, left: dragPos.x, right: "auto" } : undefined}
                    >
                        <div className="pdf-live-preview__header" onMouseDown={handleDragStart}>
                            <span className="pdf-live-preview__title">
                                <TbFileTypePdf />
                                {t("pdfSettings.previewTitle")}
                            </span>
                            <div className="pdf-live-preview__actions">
                                <button
                                    className="pdf-live-preview__btn"
                                    onClick={() => generatePreview(s)}
                                    disabled={previewing}
                                    title={t("common.refresh")}
                                >
                                    <TbRefresh className={previewing ? "spin" : ""} />
                                </button>
                                <button
                                    className="pdf-live-preview__btn"
                                    onClick={() => setModalOpen(true)}
                                    disabled={!previewUrl}
                                    title={t("pdfSettings.previewPdf")}
                                >
                                    <TbMaximize />
                                </button>
                                <button
                                    className="pdf-live-preview__btn pdf-live-preview__close-btn"
                                    onClick={() => setPreviewOpen(false)}
                                    title={t("common.close")}
                                >
                                    <TbX />
                                </button>
                            </div>
                        </div>
                        <div className="pdf-live-preview__body">
                            {previewing && (
                                <div className="pdf-live-preview__loading">
                                    <span className="pdf-live-preview__spinner" />
                                    <span className="pdf-live-preview__loading-text">{t("pdfSettings.recreating")}</span>
                                </div>
                            )}
                            {previewUrl
                                ? <iframe
                                    className="pdf-live-preview__frame"
                                    src={previewUrl}
                                    title={t("pdfSettings.previewTitle")}
                                />
                                : !previewing && (
                                    <div className="pdf-live-preview__empty">
                                        <TbFileTypePdf />
                                        <span>{t("pdfSettings.generating")}</span>
                                    </div>
                                )
                            }
                        </div>
                    </div>

                </div>{/* end .pdf-settings-layout */}
            </div>
        </>
    );
}
